// Momos — Factory Process Decoder (factory_files_decoder, Hindi).
// 16fps · 1280×704 · ~644.5s. All white, no danda. Mirrors MaggiFullPreview.
// Logo composited at the ffmpeg stage (modal_factory_render spec.add_logo). Do NOT bake it.
// CTA channel = "FACTORY PROCESS DECODER" (channel_config.channel_name).
// Every anchor locked on its spoken value/keyword start_s from word_timestamps_hindi.
import React from "react";
import { AbsoluteFill, Sequence, useVideoConfig } from "remotion";
import { PaletteProvider, BigStat, Tag } from "./overlayKit";
import { MOMOS_PALETTE } from "./MomosFullPreviewPalette";
import { CTASubscribeBell } from "./cta/CTASubscribeBell";
import { CTACombinedBar } from "./cta/CTACombinedBar";

// fps-safe: CTA Sequence frames derive from the composition's actual fps, so the
// SAME component is correct at 16fps (0.5 preview) AND 24fps (4K render). Component
// internals (Tag/BigStat) already use useVideoConfig.
export const MomosFullPreview: React.FC = () => {
  const { fps } = useVideoConfig();
  const f = (s: number) => Math.round(s * fps);
  return (
  <PaletteProvider value={MOMOS_PALETTE}>
    <AbsoluteFill>

      {/* 00:26 — branded factories under FSSAI's strict rules. */}
      <Tag inS={26.405} outS={30.5} label="FSSAI REGULATED" color="primary" />

      {/* 00:46 — Mohali raid: animal head + rotten meat found in a local momo factory. */}
      <Tag inS={46.202} outS={50.5} label="MOHALI · FACTORY RAID" color="primary" />

      {/* 01:09 — narration says "subscribe the channel" → channel-crafted CTA. */}
      <Sequence from={f(69.681)} durationInFrames={f(4.0)} layout="none">
        <CTASubscribeBell channel="FACTORY PROCESS DECODER" />
      </Sequence>

      {/* 01:33 — Dolma Aunty's Lajpat Nagar stall, 1994. Name + year. */}
      <Tag inS={93.0} outS={97.0} label="LAJPAT NAGAR · 1994" color="primary" />

      {/* 01:53 — China made 1.17 M MT of frozen dumplings (2018). */}
      <BigStat value={1.17} suffix=" M MT" sub="CHINA · 2018" valueS={113.845} decimals={2} outS={118.5} />

      {/* 02:06 — Wow Momo ≈ 5 lakh momos a day. */}
      <BigStat value={5} suffix=" LAKH / DAY" sub="WOW MOMO" valueS={126.333} decimals={0} outS={131.0} />

      {/* 02:16 — Step one: raw material dock check. */}
      <Tag inS={136.851} outS={141.5} label="STEP 1 · RAW MATERIAL" color="primary" />

      {/* 02:32 — FSSAI: fresh raw material received at ≤ 5°C. */}
      <BigStat value={5} suffix=" °C" sub="FRESH INTAKE" valueS={152.815} decimals={0} outS={157.5} />

      {/* 02:40 — frozen meat arrives at −18°C. */}
      <BigStat value={18} prefix="−" suffix=" °C" sub="FROZEN MEAT" valueS={160.214} decimals={0} outS={164.5} />

      {/* 03:15 — Step two: dough mixing (automatic dough feeder). */}
      <Tag inS={195.186} outS={199.5} label="STEP 2 · DOUGH MIXING" color="primary" />

      {/* 03:33 — Step three: hydro-extractor wrings water from the cabbage. */}
      <Tag inS={213.636} outS={218.0} label="STEP 3 · HYDRO-EXTRACTOR" color="primary" />

      {/* 04:11 — Step four: molding & pleating machine. */}
      <Tag inS={251.201} outS={255.5} label="STEP 4 · MOLD + PLEAT" color="primary" />

      {/* 04:52 — double-lane machine ≈ 20,000 momos per hour. */}
      <BigStat value={20} suffix=" K / HR" valueS={292.892} decimals={0} outS={295.8} />

      {/* 04:56 — 300+ pieces a minute. */}
      <BigStat value={300} suffix=" / MIN" valueS={296.271} decimals={0} outS={300.2} />

      {/* 06:33 — Delhi Cantt dog-meat case raid. */}
      <Tag inS={393.878} outS={398.0} label="DELHI CANTT RAID" color="primary" />

      {/* 06:54 — 20 momo shops sealed overnight. */}
      <BigStat value={20} suffix=" SHOPS SEALED" sub="DELHI CANTT" valueS={414.720} decimals={0} outS={419.0} />

      {/* 08:23 — the factory's most dangerous part: the kill step. */}
      <Tag inS={503.820} outS={507.8} label="KILL STEP" color="primary" />

      {/* 08:37 — non-veg core must reach ≥ 75°C. */}
      <BigStat value={75} suffix=" °C" sub="CORE · NON-VEG" valueS={517.852} decimals={0} outS={521.6} />

      {/* 08:42 — held for a full 15 seconds. */}
      <BigStat value={15} suffix=" SEC" valueS={521.986} decimals={0} outS={525.5} />

      {/* 09:10 — cross-promo: the Maggi / Nestlé recall video. */}
      <Tag inS={550.300} outS={554.5} label="WATCH: MAGGI EXPOSÉ" color="primary" />

      {/* 09:17 — IQF: Individually Quick Frozen tunnel at −18°C. */}
      <Tag inS={557.590} outS={562.2} label="IQF · −18 °C TUNNEL" color="primary" />

      {/* 09:38 — every momo passes through an X-ray machine. */}
      <Tag inS={578.074} outS={582.5} label="X-RAY INSPECTION" color="primary" />

      {/* 10:01 — cold-chain systems = 4% of global greenhouse gases. */}
      <BigStat value={4} suffix="%" sub="COLD-CHAIN GHG" valueS={601.750} decimals={0} outS={605.5} />

      {/* 10:19 — comment ask → channel-crafted LIKE·SHARE·SUBSCRIBE bar. */}
      <Sequence from={f(619.0)} durationInFrames={f(5.5)} layout="none">
        <CTACombinedBar />
      </Sequence>

    </AbsoluteFill>
  </PaletteProvider>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
